import { useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
  Image,
} from 'react-native';
import { Mail, Lock, Cat } from 'lucide-react-native';
import { CatFishPattern } from '@/components/ui';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';

import { Button, Card, TextField } from '@/components/ui';
import { useAuth } from '@/stores/authStore';
import { colors, typography } from '@/theme/tokens';

import logo from '@assets/brand/logo.jpeg';

export function LoginScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const signIn = useAuth((s) => s.signIn);
  const loading = useAuth((s) => s.loading);

  const onSubmit = async () => {
    setLocalError(null);
    if (!email || !password) {
      setLocalError('Ingresá tu email y contraseña.');
      return;
    }
    try {
      await signIn(email, password);
    } catch (e: any) {
      setLocalError(e?.message ?? 'No se pudo iniciar sesión.');
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: colors.crema[50] }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            flex: 1,
            flexDirection: wide ? 'row' : 'column',
            minHeight: '100%',
          }}
        >
          {/* Lado izquierdo - branding */}
          <View
            style={{
              ...(wide ? { flex: 1.1 } : { flexShrink: 0 }),
              backgroundColor: colors.magenta[800],
              padding: wide ? 64 : 24,
              paddingVertical: wide ? 64 : 30,
              justifyContent: wide ? 'space-between' : 'flex-start',
              gap: wide ? 0 : 12,
              // @ts-ignore web only
              backgroundImage: `radial-gradient(800px 400px at 110% -10%, ${colors.magenta[500]}55, transparent 60%), radial-gradient(600px 400px at -10% 110%, ${colors.magenta[600]}55, transparent 60%)`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <CatFishPattern variant="magenta" opacity={0.35} tileSize={340} />
            <Animated.View entering={FadeInDown.duration(500)}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <View
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    backgroundColor: 'rgba(255,255,255,0.16)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Cat size={22} color={colors.white} />
                </View>
                <Text
                  style={{
                    color: colors.white,
                    fontFamily: typography.display.fontFamily,
                    fontSize: 22,
                    fontWeight: typography.weights.extrabold,
                    letterSpacing: -0.5,
                  }}
                >
                  CliniCAT
                </Text>
              </View>
            </Animated.View>

            <Animated.View entering={FadeInUp.duration(700).delay(100)} style={{ gap: wide ? 18 : 8, maxWidth: 460 }}>
              <Text
                style={{
                  color: colors.white,
                  fontFamily: typography.display.fontFamily,
                  fontSize: wide ? 44 : 25,
                  lineHeight: wide ? 50 : 30,
                  fontWeight: typography.weights.extrabold,
                  letterSpacing: -1,
                }}
              >
                Toda la clínica, ordenada.
              </Text>
              {wide ? (
                <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: 16, lineHeight: 24 }}>
                  Inventario, contabilidad y operación interna en un solo lugar. Pensado para que sea
                  tan suave de usar como acariciar a un gato.
                </Text>
              ) : null}
            </Animated.View>

            {wide ? (
              <Animated.View entering={FadeInUp.duration(700).delay(250)}>
                <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, letterSpacing: 0.5 }}>
                  Calle Los Motojobobos #2015 · +591 60008504 · facebook.com/clinicat.bo
                </Text>
              </Animated.View>
            ) : null}
          </View>

          {/* Lado derecho - formulario */}
          <View
            style={{
              flex: 1,
              padding: wide ? 64 : 20,
              paddingTop: wide ? 64 : 28,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Animated.View entering={FadeInUp.duration(600).delay(150)} style={{ width: '100%', maxWidth: 420 }}>
              <Card variant="elevated" padding={wide ? 28 : 22} style={{ gap: wide ? 24 : 20 }}>
                <View style={{ gap: 6 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <Image source={logo} style={{ width: 48, height: 48, borderRadius: 12 }} />
                    <View>
                      <Text
                        style={{
                          fontFamily: typography.display.fontFamily,
                          fontSize: wide ? 24 : 20,
                          fontWeight: typography.weights.extrabold,
                          color: colors.ink[900],
                          letterSpacing: -0.6,
                        }}
                      >
                        Bienvenida de nuevo
                      </Text>
                      <Text style={{ fontSize: 14, color: colors.ink[500], marginTop: 2 }}>
                        Iniciá sesión para continuar
                      </Text>
                    </View>
                  </View>
                </View>

                <View style={{ gap: 16 }}>
                  <TextField
                    label="Usuario"
                    placeholder="nombre.apellido"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="username"
                    autoCorrect={false}
                    iconLeft={<Mail size={18} color={colors.ink[400]} />}
                  />
                  <TextField
                    label="Contraseña"
                    placeholder="••••••••"
                    value={password}
                    onChangeText={setPassword}
                    password
                    autoCapitalize="none"
                    autoComplete="password"
                    iconLeft={<Lock size={18} color={colors.ink[400]} />}
                    onSubmitEditing={onSubmit}
                  />
                </View>

                {localError ? (
                  <View
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      backgroundColor: colors.danger[50],
                      borderWidth: 1,
                      borderColor: '#FBD0D0',
                    }}
                  >
                    <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>
                      {localError}
                    </Text>
                  </View>
                ) : null}

                <Button label="Ingresar" onPress={onSubmit} loading={loading} fullWidth size="lg" />

                <Text style={{ textAlign: 'center', fontSize: 12, color: colors.ink[400] }}>
                  ¿Primera vez? Ingresá tu usuario y la contraseña que vas a usar: esa queda como tuya.
                  {'\n'}Si la olvidás, pedile al administrador que la restablezca.
                </Text>
              </Card>
            </Animated.View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
